<template>
	<k-dialog v-model="is_open" persistent>
		<k-card class="print_preview_card">
			<k-card-title class="pb-2 pt-2 title_divider">
				<b style="font-weight: 600; font-size: x-large;">Print Preview</b>
				<k-spacer/>
				<div>
					<k-btn 
						small 
						class="k-tight-btn mr-4" 
						v-bind="modal_context.framework_color_bind_button"
						@click="print_rubric"
					>
						<k-icon small class="mr-2">fas fa-print fa-fw</k-icon>
						Print
					</k-btn>
					<k-tooltip bottom>
						<template v-slot:activator="{on}">
							<k-btn v-on="on" x-small fab color="#222" dark accessible-label="Close rubric print preview" @click="close_modal">
								<k-icon>fas fa-xmark</k-icon>
							</k-btn>
						</template>
						Close
					</k-tooltip>
				</div>
			</k-card-title>
			<k-card-text style="height: 90%; overflow: hidden;">
				<div style="display: flex; width: 100%; gap: 16px; height: 100%;">
					<div style="min-width:25vw;">
						<h3 class="mt-3" style="font-weight: 600;">Information to Include:</h3>
						<div class="mr-6" style="flex:1 1 auto">
							<k-checkbox class="mt-0 pt-0" v-model="print_preview_options.title" label="Title" hide-details></k-checkbox>
							<k-checkbox class="mt-0 pt-0" v-model="print_preview_options.description" label="Description" hide-details></k-checkbox>
							<k-checkbox class="mt-0 pt-0" v-model="print_preview_options.notes" label="Notes" hide-details></k-checkbox>
						</div>
						<h3 class="mt-3" style="font-weight: 600;">Layout</h3>
						<!-- horizontal radio group, without message -->
						<k-radio-group v-model="layout" class="mt-0 pt-0" row hide-details>
							<k-radio class="mt-0 pt-0" label="Table" value="table"></k-radio>
							<k-radio class="mt-0 pt-0" label="List" value="list"></k-radio>
						</k-radio-group>
						<h3 class="mt-3" style="font-weight: 600;">Font Size</h3>
						<k-radio-group v-model="font_size" class="mt-0 pt-0" row hide-details>
							<k-radio class="mt-0 pt-0" label="Small" value="small"></k-radio>
							<k-radio class="mt-0 pt-0" label="Medium" value="medium"></k-radio>
							<k-radio class="mt-0 pt-0" label="Large" value="large"></k-radio>
						</k-radio-group>
						<h3 class="mt-3" style="font-weight: 600;">Criteria to Include:</h3>
						<div class="mr-6" style="flex:1 1 auto">
							<k-checkbox 
								v-for="each in stateful_criteria"
								v-model="each.selected"
								@click="on_selected_criteria_change"
								class="mt-0 pt-0" 
								:label="each.description" 
								hide-details
							></k-checkbox>
						</div>
					</div>
					<div class="mt-3" style="flex:1 1 auto">
						<iframe id="print_preview" style="height: 100%; width: 100%; border: transparent;"></iframe>
					</div>
				</div>
			</k-card-text>
		</k-card>
	</k-dialog>
</template>

<script>
export default {
	model: {
		prop: 'is_open',
		event: 'change'
	},
	props: {
		is_open: {
			type: Boolean,
			default: false
		},
		modal_context: {
			type: Object,
			default: () => ({})
		},
	},
	data() {
		return {
			print_preview_options: {
				title: true,
				description: false,
				notes: false,
			},
			layout: 'table',
			font_size: 'medium',
		}
	},
	methods: {
		close_modal() {
			this.$emit('change', false) 
		},
		print_rubric() {
			// get the iframe element and print it
			const iframe = document.getElementById('print_preview')
			if (iframe && iframe.contentWindow) {
				iframe.contentWindow.focus() // Focus the iframe
				iframe.contentWindow.print() // Print the content of the iframe
			} else {
				console.error("Print preview iframe not found or not ready!")
			}
		},
		on_selected_criteria_change() {
			this.updatePrintPreview()
		},
		updatePrintPreview() {
			if (!this.is_open) {
				return 
			}
			setTimeout(() => {
				const iframe = document.getElementById('print_preview')
				if (!iframe || !iframe.contentWindow) {
					console.error("Print preview iframe not found or not ready!")
					return
				}

				const printDoc = iframe.contentWindow.document
				printDoc.open() // Open the document context for writing

				const selectedCriteria = this.stateful_criteria.filter(x => x.selected)
				printDoc.write(`
					<!DOCTYPE html>
					<html>
					<head>
					<title>Print Preview - ${this.modal_context.title}</title>
					<style>
						/* Basic styling */
						body { 
							font-family: Arial, sans-serif; 
							margin: 0; /* Reset body margin */
							padding: 0; /* Reset body padding */
							line-height: 1.2;
							font-size: ${this.font_size_in_px}; /* Set font size */
						}
						table { 
							width: 100%; 
							border-collapse: collapse; 
							margin-top: 15px; /* Add some space above the table */
						}
						td, th { 
							border: 1px solid #ccc; 
							padding: 8px; 
							text-align: left;
							vertical-align: top; /* Align content top */
						}
						th {
							background-color: #f2f2f2; /* Light grey header */
						}
						h1, h4, p {
							margin-bottom: 0.5em; /* Consistent spacing */
						}
						hr {
							margin: 15px 0;
							border: 0;
							border-top: 1px solid #eee;
						}

						/* --- A4 Page Styling --- */
						@page {
							size: Letter; /* Define the page size */
							margin: 1in; /* Standard A4 margins (adjust as needed) */
						}

						/* --- Optional: Simulate page margins visually in the iframe --- */
						/* Apply padding to the body to mimic page margins visually */
						body {
							padding: 1in; /* Match the @page margin for visual consistency */
							box-sizing: border-box; /* Include padding in width/height calculations */
							width: 210mm; /* A4 width */
							min-height: 2mm; /* A4 height - Ensures at least one page height */
							/* Add a subtle border/shadow to visualize the page edge */
							border: 1px solid #eee; 
							box-shadow: 0 0 5px rgba(0,0,0,0.1);
							margin: 10px auto; /* Center the 'page' visually if iframe container is wider */
						}

						/* Prevent table rows/headings from breaking across pages */
						thead { display: table-header-group; } /* Repeat header on new pages */
						tbody tr { page-break-inside: avoid; } /* Try to keep rows together */
						h1, h4 { page-break-after: avoid; } /* Try to keep headings with content below */

                        /* Styling for the list layout */
                        #rubric-list {
                            margin-top: 15px;
                        }
                        #rubric-list .criterion {
                            margin-bottom: 20px;
                            padding: 10px;
                            border: 1px solid #ccc;
                            border-radius: 5px;
                        }
                        #rubric-list h4 {
                            margin-bottom: 5px;
                        }
                        #rubric-list ul {
                            list-style-type: disc;
                            padding-left: 20px;
                        }
                        #rubric-list li {
                            margin-bottom: 5px;
                        }
                        #rubric-list li:last-child {
                            margin-bottom: 0;
                        }
                        
                        #rubric-list .criterion {
                            page-break-inside: avoid;
                            page-break-after: avoid;
                        }
                        
						
					</style>
					</head>
					<body>
					${this.print_preview_options.title ? `<h1 id="title">${this.modal_context.title}</h1>` : ''}
					${this.print_preview_options.description && this.modal_context.description ? `<div id="description"><h4>Description:</h4><div style="white-space: pre-wrap;">${this.modal_context.description}</div></div>` : ''}
					${this.print_preview_options.notes && this.modal_context.notes ? `<div id="notes"><h4>Notes:</h4><div style="white-space: pre-wrap;">${this.modal_context.notes}</div></div>` : ''}
					${(this.print_preview_options.description && this.modal_context.description) || (this.print_preview_options.notes && this.modal_context.notes) ? '<hr>' : ''}
                    ${selectedCriteria.length > 0 ? 
						(this.layout === 'table' ? `
                        <table id="rubric-table">
                            <thead>
                                <tr>
                                    <th>Criterion</th>
                                    ${selectedCriteria[0].CFRubricCriterionLevels.map(level => `<th>${level.quality || 'Level'}</th>`).join('')}
                                </tr>
                            </thead>
                            <tbody>
                                ${selectedCriteria.map(criterion => `
                                    <tr>
                                        <td>${criterion.description || 'N/A'}</td>
                                        ${criterion.CFRubricCriterionLevels.map(level => `<td style="white-space: pre-wrap;">${level.description || ''}</td>`).join('')}
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>`
							: `
                        <div id="rubric-list">
                            ${selectedCriteria.map(criterion => `
                                <div class="criterion">
                                    <h4>${criterion.description || 'N/A'}</h4>
                                    <ul>
                                    ${criterion.CFRubricCriterionLevels.map(level => `
                                        <li>
                                            <strong>${level.quality}</strong> - ${level.description || ''}
                                        </li>
                                    `).join('')}
                                    </ul>
                                </div>
                            `).join('')}
                        </div>`)
						: '<p>No criteria selected or available.</p>'}
					</body>
					</html>
				`)
				printDoc.close()
			}, 20)
		},
	},
	computed: {
		/**
         *  We use this to filter out rows with no text. There is
         *  no need to print them.
         */
		criteria_with_content() {
			return this.modal_context.criteria.filter(x => {
				if (x.description) {
					return true
				}
				if (x.children) {
					return x.children.some(y => y.description)
				}
				return false
			})
		},

		stateful_criteria() {
			return  this.criteria_with_content.map(x => {
				x.selected = true
				return x
			})
		},

		font_size_in_px() {
			switch (this.font_size) {
				case 'small':
					return '8pt'
				case 'medium':
					return '10pt'
				case 'large':
					return '12pt'
				default:
					return '10pt' // Default size
			}
		}
	},
	watch: {
		is_open: {
			handler(newVal) {
				if (newVal) {
					// Call the update method when dialog opens
					this.updatePrintPreview()
				} else {
					// Optional: Reset iframe content when closed to free memory
					const iframe = document.getElementById('print_preview')
					if (iframe && iframe.contentWindow) {
						try {
							iframe.src = 'about:blank' // Clear content // Reset height
						} catch(e) {
							console.warn("Could not reset iframe src.")
						}
					}
				}
			}
		},
		print_preview_options: {
			handler(newOptions, oldOptions) {
				// Only update if the preview is currently open
				if (this.is_open) {
					console.log('Print options changed, updating preview...')
					this.updatePrintPreview()
				}
			},
			deep: true // Needed because it's an object
		},
		layout: {
			handler(newLayout, oldLayout) {
				// Only update if the preview is currently open
				if (this.is_open) {
					console.log('Layout changed, updating preview...')
					this.updatePrintPreview()
				}
			}
		},
		font_size_in_px: {
			handler(newSize, oldSize) {
				// Only update if the preview is currently open
				if (this.is_open) {
					console.log('Font size changed, updating preview...')
					this.updatePrintPreview()
				}
			}
		},
	}
}
</script>

<style scoped>
.print_preview_card {
    height: 80vh;
}

.title_divider {
    border-bottom:1px solid #ccc
}
</style></template>
